import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Reusable delete confirmation modal with warning styling
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {string} entityType - Type of entity being deleted (e.g., "user", "group")
 * @param {string} entityName - Name of the entity being deleted
 * @param {string} title - Custom title (defaults to "Delete {entityType} - {entityName}?")
 * @param {string} message - Custom message (defaults to standard delete message)
 * @param {string} confirmLabel - Custom confirm button text (defaults to "Delete")
 * @param {string} cancelLabel - Custom cancel button text (defaults to "Cancel")
 * @param {Function} onConfirm - Callback when confirm button is clicked
 * @param {Function} onCancel - Callback when cancel button is clicked or modal is closed
 * @param {boolean} loading - Shows loading state on confirm button
 */
function ConfirmDeleteModal({
  isOpen,
  entityType,
  entityName,
  onConfirm,
  onCancel,
  loading = false,
  title,
  message,
  confirmLabel,
  cancelLabel
}) {
  const resolvedType = entityType || 'item';
  const resolvedTitle = title || `Delete ${resolvedType} - ${entityName}?`;
  const resolvedConfirmLabel = confirmLabel || 'Delete';
  const resolvedCancelLabel = cancelLabel || 'Cancel';
  const resolvedMessage = message || `Are you sure you want to permanently delete this ${resolvedType}?`;

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      {/* Subtle colored top border for visual indication */}
      <Box
        sx={{
          height: 4,
          backgroundColor: 'error.main'
        }}
      />

      <DialogTitle
        sx={{
          pt: 3,
          pb: 2,
          px: 3,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {resolvedTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'error.lighter',
              color: 'error.main',
              flexShrink: 0
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 28 }} />
          </Box>

          <Box sx={{ flex: 1, pt: 0.5 }}>
            {entityName && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'text.primary'
                }}
              >
                {entityName}
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: 'text.secondary' }}
            >
              {resolvedMessage}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'error.main',
                fontSize: '0.875rem'
              }}
            >
              This action cannot be undone.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          {resolvedCancelLabel}
        </Button>

        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {loading ? 'Deleting...' : resolvedConfirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDeleteModal;